/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  Edits,
  DialogueMessage,
  LoadStatus,
  DialogueMessageType,
  AnimationType,
} from "../../../types";

export interface UseWithDialogues {
  dialogues: Record<string, DialogueMessage>;
  dialogueEdits: Record<string, Edits<DialogueMessage>>;
  dialogueLoadStatus: LoadStatus;
  loadAllDialogues: () => Promise<DialogueMessage[]>;
  createDialogue: (
    newData: Partial<DialogueMessage>
  ) => Promise<DialogueMessage>;
  deleteDialogue: (dialogue: DialogueMessage) => Promise<void>;
  updateDialogue: (
    newData: Partial<DialogueMessage>
  ) => Promise<DialogueMessage>;
}

export default function useWithDialogues(): UseWithDialogues {
  const { dialogues, dialogueLoadStatus, dialogueEdits } = useAppSelector(
    (state) => state.dialogues
  );
  const dispatch = useAppDispatch();

  async function loadAllDialogues(): Promise<DialogueMessage[]> {
    if (dialogueLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadDialogues()).unwrap();
  }

  async function createDialogue(
    data: Partial<DialogueMessage>
  ): Promise<DialogueMessage> {
    if (!data.text) throw new Error("text required");
    return await dispatch(
      actions.addOrUpdateDialogue({
        ...data,
        id: data.id,
        type: data.type || DialogueMessageType.None,
        animation: data.animation || AnimationType.None,
        deleted: false,
      })
    ).unwrap();
  }

  async function deleteDialogue(dialogue: DialogueMessage): Promise<void> {
    if (!dialogue.id) throw new Error("id required");
    await dispatch(actions.deleteDialogue(dialogue.id));
  }

  async function updateDialogue(
    data: Partial<DialogueMessage>
  ): Promise<DialogueMessage> {
    return await dispatch(actions.addOrUpdateDialogue(data)).unwrap();
  }

  return {
    dialogues,
    dialogueEdits,
    dialogueLoadStatus,
    loadAllDialogues,
    createDialogue,
    deleteDialogue,
    updateDialogue,
  };
}
